import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'Από μεταφόρτωση σε αποτέλεσμα — μία εξάσκηση 15 λεπτών',
    summary: 'Μια ολοκληρωμένη εκτέλεση από άκρο σε άκρο: δημιουργήστε μια κοινότητα, μεταφορτώστε ένα αρχείο, αποκτήστε μια πρόβλεψη και ένα πρώτο αποτέλεσμα βελτιστοποίησης.',
    body: (
      <>
        <p>
          Αυτό το άρθρο συνδέει τα πάντα μεταξύ τους. Ακολουθήστε το μία φορά και θα έχετε
          χρησιμοποιήσει κάθε εργαλείο τουλάχιστον μία φορά. Προϋποθέτει ότι έχετε έτοιμο ένα αρχείο
          ωριαίας κατανάλωσης για ένα κτίριο.
        </p>

        <ol>
          <li>
            <strong>Δημιουργήστε μια κοινότητα.</strong> Πίνακας → <em>Κοινότητες</em> →
            {' '}<em>Νέα κοινότητα</em>. Δώστε ένα όνομα και επιλέξτε χώρα και νόμισμα. Αποθηκεύστε.
          </li>
          <li>
            <strong>Προσθέστε τουλάχιστον ένα κτίριο.</strong> Ανοίξτε την κοινότητα → <em>Κτίρια</em> →
            {' '}<em>Προσθήκη κτιρίου</em>. Όνομα κτιρίου, τύπος και ταχυδρομικός κώδικας είναι αρκετά για να ξεκινήσετε.
          </li>
          <li>
            <strong>Μεταφορτώστε το αρχείο φορτίου σας.</strong> Κοινότητα → <em>Διαχείριση Δεδομένων</em> → επιλέξτε
            {' '}<em>Building Load Profile</em>, επιλέξτε το κτίριο, αποθέστε το CSV. Δείτε
            {' '}<ArticleLink to="data-upload-how">Μεταφόρτωση αρχείου, βήμα προς βήμα</ArticleLink> αν
            η σελίδα είναι νέα για εσάς.
          </li>
          <li>
            <strong>(Συνιστάται) Μεταφορτώστε ένα αρχείο τιμολογίου.</strong> Χωρίς τιμές, τα αποτελέσματα κόστους θα
            είναι ενδεικτικά μόνο. Δείτε <ArticleLink to="data-tariff">Αρχεία τιμολογίου</ArticleLink>.
          </li>
          <li>
            <strong>Εκτελέστε μια πρόβλεψη.</strong> Πλευρική γραμμή κοινότητας → <em>Πρόβλεψη</em>. Η σελίδα έχει
            δύο καρτέλες — <em>Βραχυπρόθεσμα (Day-Ahead)</em> και <em>Μακροπρόθεσμα (μηνιαία)</em>. Επιλέξτε ένα
            κτίριο, κάντε κλικ στο <em>Εκτέλεση πρόβλεψης</em>. Δείτε
            {' '}<ArticleLink to="analysis-forecast">Εκτέλεση πρόβλεψης</ArticleLink>.
          </li>
          <li>
            <strong>Εκτελέστε μια βελτιστοποίηση.</strong> Πλευρική γραμμή κοινότητας → <em>Βελτιστοποίηση</em> (η
            ίδια η σελίδα τιτλοφορείται «Βελτιστοποίηση»). Επιλέξτε έναν στόχο (π.χ. <em>Ελαχιστοποίηση κόστους</em>)
            και κάντε κλικ στο <em>Εκτέλεση βελτιστοποίησης Day-Ahead</em>. Δείτε
            {' '}<ArticleLink to="analysis-optimisation">Εκτέλεση βελτιστοποίησης</ArticleLink>.
          </li>
          <li>
            <strong>Διαβάστε τα αποτελέσματα.</strong> Αριθμοί, γραφήματα και οικονομίες εμφανίζονται στην ίδια σελίδα.
            Τι σημαίνει κάθε KPI περιγράφεται στο <ArticleLink to="results-glossary">γλωσσάρι</ArticleLink>·
            ο τρόπος ερμηνείας κάθε γραφήματος βρίσκεται στην υπόλοιπη ενότητα.
          </li>
        </ol>

        <Tip>
          Η πλατφόρμα εμφανίζει σαφώς επισημασμένα ενδεικτικά αποτελέσματα εάν μια υπηρεσία δεν είναι
          διαθέσιμη ή τα απαιτούμενα δεδομένα δεν έχουν μεταφορτωθεί. Εάν ένα αποτέλεσμα φαίνεται
          ύποπτα στρογγυλό (π.χ. ακριβώς 100 €/χρόνο), ελέγξτε τη σελίδα για ειδοποίηση «δεδομένα παραδείγματος».
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Εκτέλεση πρόβλεψης',
    summary: 'Προβλέψτε τις επόμενες ώρες/ημέρες κατανάλωσης ή παραγωγής από ένα κτίριο ή κοινότητα.',
    body: (
      <>
        <p>
          Μια <strong>πρόβλεψη</strong> χρησιμοποιεί τα ιστορικά δεδομένα σας και προβλέπει το μελλοντικό
          φορτίο ή την παραγωγή. Χρησιμοποιήστε την για τον προγραμματισμό λειτουργιών, για την οδήγηση
          βελτιστοποίησης Day-Ahead, ή απλώς για να δείτε πώς θα είναι το αύριο — ή το επόμενο τρίμηνο.
        </p>

        <h5 className="mt-3">Πριν την εκτελέσετε</h5>
        <ul>
          <li>Η κοινότητα διαθέτει τουλάχιστον ένα κτίριο με μεταφορτωμένο <strong>Building Load Profile</strong>
            (περισσότερο ιστορικό → καλύτερη πρόβλεψη — συνιστώνται τουλάχιστον 3 μήνες δεδομένων για
            αξιόπιστες προβλέψεις, ιδανικά ένας χρόνος).</li>
          <li>Για προβλέψεις <em>ηλιακής</em> ή <em>αιολικής</em> ενέργειας, χρειάζεστε επίσης ένα αρχείο
            παραγωγής (κοινοτικό προφίλ Φ/Β ή αιολικής, ή το ηλιακό προφίλ οροφής του κτιρίου). Ένα
            {' '}<ArticleLink to="data-weather">αρχείο καιρού</ArticleLink> είναι προαιρετικό αλλά βελτιώνει
            την ακρίβεια κατά 15–30%.</li>
        </ul>

        <h5 className="mt-3">Βήμα προς βήμα</h5>
        <ol>
          <li>Ανοίξτε την κοινότητα → επιλέξτε <strong>Πρόβλεψη</strong> στην πλευρική γραμμή.</li>
          <li>Επιλέξτε τη <strong>λειτουργία</strong> από τις δύο καρτέλες στην κορυφή της σελίδας:
            <ul>
              <li><strong>Βραχυπρόθεσμα (Day-Ahead)</strong> — προβλέπει τις επόμενες 24 ώρες από μια επιλεγμένη
                ημερομηνία στόχο (έως 7 ημέρες μπροστά).</li>
              <li><strong>Μακροπρόθεσμα (μηνιαία)</strong> — προβάλλει μηνιαία στοιχεία σε ορίζοντα που
                επιλέγετε: <em>3 μήνες</em>, <em>6 μήνες</em> ή <em>12 μήνες</em>.</li>
            </ul>
          </li>
          <li>Επιλέξτε το/τα <strong>πόρο/πόρους</strong> για πρόβλεψη: το φορτίο ενός κτιρίου, την κοινοτική
            παραγωγή Φ/Β, ή την κοινοτική παραγωγή αιολικής. Μπορείτε να επιλέξετε περισσότερα από ένα.</li>
          <li>Κάντε κλικ στο <strong>Εκτέλεση πρόβλεψης</strong> (η ετικέτα του κουμπιού προσαρμόζεται
            στον αριθμό των επιλεγμένων πόρων). Ένας περιστρεφόμενος δείκτης εμφανίζει <em>Εκτέλεση πρόβλεψης…</em>
            κατά τη διάρκεια της επεξεργασίας.</li>
          <li>Τα αποτελέσματα εμφανίζονται στο δεξί πλαίσιο. Δείτε
            {' '}<ArticleLink to="results-forecast">Ανάγνωση του γραφήματος πρόβλεψης</ArticleLink>
            {' '}για το τι σημαίνει κάθε γραμμή και ζώνη.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="Η σελίδα Προβλέψεων: επιλέξτε καρτέλα, επιλέξτε πόρους, εκτελέστε."
        />

        <Tip>
          Εάν η ίδια πρόβλεψη φαίνεται πολύ διαφορετική από εκτέλεση σε εκτέλεση, πιθανώς έχετε
          ελάχιστα δεδομένα εισόδου. Προσθέστε περισσότερο ιστορικό και εκτελέστε ξανά. Δείτε
          {' '}<ArticleLink to="tb-forecast-flat">«Η πρόβλεψή μου φαίνεται επίπεδη ή λανθασμένη»</ArticleLink>
          {' '}στην Αντιμετώπιση Προβλημάτων εάν τα αποτελέσματα εξακολουθούν να φαίνονται εσφαλμένα.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Εκτέλεση βελτιστοποίησης (Βελτιστοποίηση)',
    summary: 'Αποφασίστε πότε να εισάγετε, να εξάγετε, να φορτίζετε ή να εκφορτίζετε — για ελαχιστοποίηση κόστους ή μεγιστοποίηση αυτάρκειας.',
    body: (
      <>
        <p>
          Το εργαλείο <strong>Βελτιστοποίησης</strong> βρίσκει το βέλτιστο πρόγραμμα για τους ευέλικτους
          πόρους της κοινότητάς σας — πότε να φορτίζετε ή να εκφορτίζετε μια μπαταρία, πότε να φορτίζετε
          EVs, πότε να εισάγετε από το δίκτυο και πότε να εξάγετε — υπό έναν στόχο που επιλέγετε εσείς.
        </p>

        <h5 className="mt-3">Πριν την εκτελέσετε</h5>
        <ul>
          <li>Μια κοινότητα με τουλάχιστον ένα μεταφορτωμένο αρχείο <strong>φορτίου</strong>.</li>
          <li>Ένα αρχείο <strong>τιμολογίου</strong> (ο βελτιστοποιητής χρειάζεται τιμές για να γνωρίζει
            τι είναι φθηνό και τι ακριβό). Δείτε <ArticleLink to="data-tariff">Αρχεία τιμολογίου</ArticleLink>.</li>
          <li>Προαιρετικό αλλά συνιστάται: ένα αρχείο <strong>παραγωγής</strong> και τυχόν
            {' '}<strong>πόροι</strong> (Φ/Β, μπαταρία, EV) καταχωρημένοι για την κοινότητα.</li>
        </ul>

        <h5 className="mt-3">Βήμα προς βήμα</h5>
        <ol>
          <li>Ανοίξτε την κοινότητα → επιλέξτε <strong>Βελτιστοποίηση</strong> στην πλευρική γραμμή.
            (Η ίδια η σελίδα τιτλοφορείται «Βελτιστοποίηση» — ίδια θέση.)</li>
          <li>Επιλέξτε έναν <strong>στόχο</strong>:
            <ul>
              <li><em>Ελαχιστοποίηση κόστους</em> — αγοράστε όταν οι τιμές είναι χαμηλές, πουλήστε όταν
                είναι υψηλές. Προεπιλογή για τις περισσότερες κοινότητες.</li>
              <li><em>Μεγιστοποίηση αυτάρκειας</em> — χρησιμοποιήστε όσο το δυνατόν περισσότερη από τη
                δική σας παραγωγή, ακόμα κι αν το κόστος είναι ελαφρώς υψηλότερο.</li>
              <li><em>Μεγιστοποίηση αυτοκατανάλωσης</em> — ελαχιστοποιήστε τις εξαγωγές της παραγωγής σας.</li>
            </ul>
          </li>
          <li>Η βελτιστοποίηση εκτελεί ένα πρόγραμμα μιας ημέρας (24 ωρών) για την επόμενη ημέρα.</li>
          <li>Ελέγξτε τη λίστα πόρων (Φ/Β, μπαταρία, φορτιστές EV). Οι ρυθμίσεις μπαταρίας, όπως
            {' '}<em>ελάχιστη/μέγιστη κατάσταση φόρτισης</em> και <em>απόδοση round-trip</em>, πρέπει να
            αντιστοιχούν στην πραγματικότητα — αλλάξτε τις εάν δεν ισχύει.</li>
          <li>Κάντε κλικ στο <strong>Εκτέλεση βελτιστοποίησης Day-Ahead</strong>. Ένας περιστρεφόμενος
            δείκτης εμφανίζει <em>Εκτέλεση βελτιστοποίησης…</em> (ενδέχεται επίσης να δείτε ένα μήνυμα
            «Επίλυση προβλήματος MILP» — αυτό είναι ο μαθηματικός επιλύτης σε λειτουργία). Μια εκτέλεση
            λίγων κτιρίων συνήθως ολοκληρώνεται σε λιγότερο από ένα λεπτό.</li>
          <li>Όταν ολοκληρωθεί, το πλαίσιο αποτελεσμάτων εμφανίζεται παρακάτω — κάρτες KPI στην κορυφή,
            ωριαία Ενεργειακό Ισοζύγιο και γραφήματα μπαταρίας από κάτω. Δείτε
            {' '}<ArticleLink to="results-optimisation-kpis">Ανάγνωση των KPI βελτιστοποίησης</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="Η σελίδα Βελτιστοποίησης: επιλέξτε στόχο, ελέγξτε τους πόρους, εκτελέστε."
        />

        <Warn>
          Εάν η εκτέλεση ολοκληρωθεί με «infeasible», οι περιορισμοί δεν μπορούν να ικανοποιηθούν
          ταυτόχρονα (για παράδειγμα, η μπαταρία είναι πολύ μικρή για τη ζήτηση). Δείτε
          {' '}<ArticleLink to="tb-optimisation-infeasible">«Η βελτιστοποίηση αναφέρει infeasible»</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
